import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (displayName?: string, phoneNumber?: string) => Promise<void>;
  getUserPhoneNumber: () => Promise<string | null>;
  sendEmailVerificationCode: (newEmail: string) => Promise<string>;
  verifyAndUpdateEmail: (newEmail: string, verificationCode: string) => Promise<void>;
  sendPhoneVerificationCode: (phoneNumber: string) => Promise<string>;
  verifyAndUpdatePhone: (phoneNumber: string, verificationCode: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const signup = async (email: string, password: string, displayName?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const updateUserProfile = async (displayName?: string, phoneNumber?: string) => {
    if (!currentUser) throw new Error('No user logged in');

    // Update Firebase Auth displayName if provided
    if (displayName !== undefined) {
      await updateProfile(currentUser, { displayName });
    }

    // Update Firestore user document with phone number
    if (phoneNumber !== undefined) {
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        phoneNumber: phoneNumber || null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  };

  const getUserPhoneNumber = async (): Promise<string | null> => {
    if (!currentUser) return null;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return userSnap.data().phoneNumber || null;
      }
    } catch (error) {
      console.error('Error fetching phone number:', error);
    }
    return null;
  };

  const sendEmailVerificationCode = async (newEmail: string): Promise<string> => {
    if (!currentUser) throw new Error('No user logged in');

    // Store the pending email in Firestore
    const userRef = doc(db, 'users', currentUser.uid);
    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
    
    await setDoc(userRef, {
      pendingEmail: newEmail,
      emailVerificationCode: verificationCode,
      emailVerificationCodeExpiry: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
    }, { merge: true });

    // Send verification email (we'll use a simple email service or Firebase Functions)
    // For now, we'll store the code and the user can verify it
    // In production, you'd send this via email service
    console.log('Verification code:', verificationCode); // Remove in production
    
    // TODO: Integrate with email service to send verification code
    return verificationCode; // For development/testing
  };

  const verifyAndUpdateEmail = async (newEmail: string, verificationCode: string) => {
    if (!currentUser) throw new Error('No user logged in');

    // Verify the code
    const userRef = doc(db, 'users', currentUser.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('User data not found');
    }

    const userData = userSnap.data();
    const storedCode = userData.emailVerificationCode;
    const expiry = userData.emailVerificationCodeExpiry?.toDate();

    if (!storedCode || storedCode !== verificationCode) {
      throw new Error('Invalid verification code');
    }

    if (expiry && expiry < new Date()) {
      throw new Error('Verification code has expired');
    }

    if (userData.pendingEmail !== newEmail) {
      throw new Error('Email does not match pending email');
    }

    // Update email in Firebase Auth
    await updateEmail(currentUser, newEmail);
    
    // Send verification email to new address
    await sendEmailVerification(currentUser);

    // Clear pending email data
    await setDoc(userRef, {
      pendingEmail: null,
      emailVerificationCode: null,
      emailVerificationCodeExpiry: null,
    }, { merge: true });
  };

  const sendPhoneVerificationCode = async (phoneNumber: string): Promise<string> => {
    if (!currentUser) throw new Error('No user logged in');

    // Store the pending phone in Firestore
    const userRef = doc(db, 'users', currentUser.uid);
    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
    
    await setDoc(userRef, {
      pendingPhone: phoneNumber,
      phoneVerificationCode: verificationCode,
      phoneVerificationCodeExpiry: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes
    }, { merge: true });

    // TODO: Integrate with SMS service (Twilio, etc.) to send verification code
    console.log('Phone verification code:', verificationCode); // Remove in production
    
    return verificationCode; // For development/testing
  };

  const verifyAndUpdatePhone = async (phoneNumber: string, verificationCode: string) => {
    if (!currentUser) throw new Error('No user logged in');

    // Verify the code
    const userRef = doc(db, 'users', currentUser.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('User data not found');
    }

    const userData = userSnap.data();
    const storedCode = userData.phoneVerificationCode;
    const expiry = userData.phoneVerificationCodeExpiry?.toDate();

    if (!storedCode || storedCode !== verificationCode) {
      throw new Error('Invalid verification code');
    }

    if (expiry && expiry < new Date()) {
      throw new Error('Verification code has expired');
    }

    if (userData.pendingPhone !== phoneNumber) {
      throw new Error('Phone number does not match pending phone');
    }

    // Update phone number in Firestore
    await setDoc(userRef, {
      phoneNumber: phoneNumber,
      pendingPhone: null,
      phoneVerificationCode: null,
      phoneVerificationCodeExpiry: null,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    getUserPhoneNumber,
    sendEmailVerificationCode,
    verifyAndUpdateEmail,
    sendPhoneVerificationCode,
    verifyAndUpdatePhone,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

